/* ==========================================================================
   Bauer & Meyer — home-sections.js
   Small helpers for the homepage / landing sections. Loaded (deferred) only by
   the sections that need them. Every definition is guarded, so the file can be
   included by several sections on one page.
   - <grid-show-more>: reveals collapsed grid items in steps, no reload
   - <lazy-video>: background video that loads only near the viewport
   - <clamp-text>: line-clamped text with a "read more" button, shown only when it overflows
   ========================================================================== */

(() => {
  if (window.__bmHomeSections) return;
  window.__bmHomeSections = true;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- <grid-show-more data-step="4"> ---------- */
  class GridShowMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-show-more]');
      if (!this.button) return;
      this.step = Number(this.dataset.step) || 4;
      this.button.hidden = this.collapsed().length === 0;
      this.button.addEventListener('click', () => this.reveal());
    }
    collapsed() {
      return Array.from(this.querySelectorAll('[data-collapsed]'));
    }
    reveal() {
      const items = this.collapsed().slice(0, this.step);
      items.forEach((item) => item.removeAttribute('data-collapsed'));
      // Move focus to the first new card so keyboard users continue where the new content starts
      const first = items[0] && items[0].querySelector('a[href]:not([tabindex="-1"]), button');
      if (first) first.focus({ preventScroll: true });
      if (this.collapsed().length === 0) this.button.hidden = true;
    }
  }
  if (!customElements.get('grid-show-more')) customElements.define('grid-show-more', GridShowMore);

  /* ---------- <lazy-video>: <video preload="none"> with <source data-src> ---------- */
  class LazyVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) this.play();
        else if (this.loaded) this.video.pause();
      }), { rootMargin: '150px 0px' });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
    play() {
      if (!this.loaded) {
        this.loaded = true;
        this.video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        this.video.load();
        this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      }
      if (reducedMotion()) return;
      const attempt = this.video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    }
  }
  if (!customElements.get('lazy-video')) customElements.define('lazy-video', LazyVideo);

  /* ---------- <clamp-text>: [data-clamp-body] is clamped in CSS; [data-clamp-toggle] expands it ----------
     The full text is always in the DOM (screen readers read all of it); the button only
     appears when the clamp actually hides text. */
  class ClampText extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-clamp-body]');
      this.button = this.querySelector('[data-clamp-toggle]');
      if (!this.body || !this.button) return;
      this.check = this.check.bind(this);
      this.button.addEventListener('click', () => {
        this.classList.add('is-expanded');
        this.button.setAttribute('aria-expanded', 'true');
        this.button.hidden = true;
      });
      this.observer = new ResizeObserver(this.check);
      this.observer.observe(this.body);
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
    check() {
      if (this.classList.contains('is-expanded')) return;
      this.button.hidden = this.body.scrollHeight <= this.body.clientHeight + 2;
    }
  }
  if (!customElements.get('clamp-text')) customElements.define('clamp-text', ClampText);
})();
